import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, Wifi, Cable, Download, Upload, Clock, Users,
  RefreshCw, ChevronDown, ChevronUp, Smartphone, Monitor,
  Gauge, TrendingUp, FileDown, X, BarChart3, Signal
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BandwidthMonitoring = ({ refreshInterval = 30000 }) => {
  const [bandwidthData, setBandwidthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchBandwidthData = async (showToast = false) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('monitoringAdminToken');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/monitoring/bandwidth/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBandwidthData(data);
        setLastUpdate(new Date());
        
        if (showToast) {
          toast.success('Bandwidth data refreshed');
        }
      }
    } catch (error) {
      console.error('Error fetching bandwidth:', error);
      if (showToast) {
        toast.error('Failed to fetch bandwidth data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchMonthlyReport = async (customerId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/bandwidth/report/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        setShowReportModal(true);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch report');
    }
  };

  const downloadPDF = async (customerId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/bandwidth/report/${customerId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Bandwidth_Report_${customerId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Report downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download report');
    }
  };

  useEffect(() => {
    fetchBandwidthData();
    const interval = setInterval(() => fetchBandwidthData(), refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Get 3D card style based on quality score
  const getCard3DStyle = (qualityColor, isActive) => {
    if (!isActive) {
      return {
        background: 'linear-gradient(145deg, #f3f4f6, #e5e7eb)',
        border: '2px solid #d1d5db',
        boxShadow: '8px 8px 16px rgba(107, 114, 128, 0.15), -4px -4px 12px rgba(255, 255, 255, 0.9)',
        transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
      };
    }
    
    const colorMap = {
      green: {
        background: 'linear-gradient(145deg, #ecfdf5, #d1fae5)',
        border: '2px solid #6ee7b7',
        boxShadow: '8px 8px 16px rgba(16, 185, 129, 0.2), -4px -4px 12px rgba(255, 255, 255, 0.9)'
      },
      blue: {
        background: 'linear-gradient(145deg, #eff6ff, #dbeafe)',
        border: '2px solid #93c5fd',
        boxShadow: '8px 8px 16px rgba(59, 130, 246, 0.2), -4px -4px 12px rgba(255, 255, 255, 0.9)'
      },
      yellow: {
        background: 'linear-gradient(145deg, #fffbeb, #fef3c7)',
        border: '2px solid #fcd34d',
        boxShadow: '8px 8px 16px rgba(245, 158, 11, 0.2), -4px -4px 12px rgba(255, 255, 255, 0.9)'
      },
      red: {
        background: 'linear-gradient(145deg, #fef2f2, #fee2e2)',
        border: '2px solid #fca5a5',
        boxShadow: '8px 8px 16px rgba(239, 68, 68, 0.2), -4px -4px 12px rgba(255, 255, 255, 0.9)'
      }
    };
    
    const style = colorMap[qualityColor] || colorMap.blue;
    return {
      ...style,
      transform: 'perspective(1000px) rotateX(2deg) rotateY(-2deg)'
    };
  };

  // Get quality badge
  const getQualityBadge = (quality) => {
    const styles = {
      Excellent: 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white',
      Good: 'bg-gradient-to-r from-blue-400 to-blue-500 text-white',
      Fair: 'bg-gradient-to-r from-amber-400 to-amber-500 text-white',
      Poor: 'bg-gradient-to-r from-red-400 to-red-500 text-white',
      Offline: 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-bold shadow-sm ${styles[quality?.rating] || styles.Offline}`}>
        {quality?.rating || 'Offline'}
      </span>
    );
  };

  return (
    <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-testid="bandwidth-monitoring-card">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-600 p-4">
        <div className="flex items-center justify-between">
          {/* Left side - Title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Activity className="h-6 w-6 text-white" />
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  Bandwidth Usage Monitoring
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                </h3>
                <p className="text-purple-100 text-sm">Real-time bandwidth & device monitoring</p>
              </div>
            </div>
          </div>
          
          {/* Right side - Stats badges */}
          <div className="flex items-center gap-3">
            {/* Total Customers */}
            <div className="bg-purple-400/30 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-purple-300/50">
              <span className="text-white font-bold text-lg">{bandwidthData?.summary?.total_customers || 0}</span>
              <span className="text-purple-100 text-xs ml-1">Customers</span>
            </div>
            
            {/* Active */}
            <div className="bg-green-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-green-400/50">
              <span className="text-white font-bold">{bandwidthData?.summary?.active_customers || 0}</span>
              <span className="text-green-100 text-xs ml-1">Active</span>
            </div>
            
            {/* Total Devices */}
            <div className="bg-blue-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-blue-400/50 flex items-center gap-1">
              <Users className="h-4 w-4 text-white" />
              <span className="text-white font-bold">{bandwidthData?.summary?.total_connected_devices || 0}</span>
              <span className="text-blue-100 text-xs">Devices</span>
            </div>
            
            {/* Avg Speed */}
            <div className="bg-indigo-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-indigo-400/50">
              <Download className="h-3 w-3 text-white inline mr-1" />
              <span className="text-white font-bold">{bandwidthData?.summary?.avg_download_mbps || 0}</span>
              <span className="text-indigo-100 text-xs ml-1">Mbps</span>
            </div>
            
            {/* Last Update */}
            {lastUpdate && (
              <div className="text-purple-100 text-xs">
                {lastUpdate.toLocaleTimeString()}
              </div>
            )}
            
            {/* Refresh Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchBandwidthData(true)}
              disabled={refreshing}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Expand/Collapse */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {expanded && (
        <div className="bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-purple-500" />
              <span className="ml-2 text-purple-600">Loading bandwidth data...</span>
            </div>
          ) : (
            <>
              {/* Customer Bandwidth Grid - 3D Cards */}
              {bandwidthData?.customers?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto pr-1">
                  {bandwidthData.customers.map((customer, idx) => {
                    const cardStyle = getCard3DStyle(customer.quality_score?.color, customer.is_active);
                    return (
                      <div 
                        key={idx}
                        className="rounded-xl p-4 transition-all duration-300 hover:scale-[1.02] cursor-pointer relative"
                        style={cardStyle}
                        onClick={() => setSelectedCustomer(selectedCustomer?.customer_id === customer.customer_id ? null : customer)}
                        data-testid={`bandwidth-customer-${idx}`}
                      >
                        {/* LIVE indicator */}
                        {customer.is_active && (
                          <div className="absolute top-2 right-2 flex items-center gap-1">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Live</span>
                          </div>
                        )}
                        
                        {/* Customer Name & IP */}
                        <div className="pr-12 mb-2">
                          <h4 className="font-bold text-gray-800 text-sm truncate" title={customer.customer_name}>
                            {customer.customer_name}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">{customer.location}</p>
                          <p className="text-xs text-purple-600 font-mono">{customer.ip_address || 'N/A'}</p>
                        </div>
                        
                        {/* Quality Badge */}
                        <div className="mb-2">
                          {getQualityBadge(customer.quality_score)}
                        </div>
                        
                        {/* Live Speed - Real-time upload/download */}
                        <div className="bg-white/50 rounded-lg p-2 mb-3">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-500 font-medium flex items-center gap-1">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                              </span>
                              Live Speed
                            </span>
                            <span className="text-gray-400">{customer.speed_test?.ping_ms}ms ping</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex items-center gap-1">
                              <Download className="h-4 w-4 text-green-500" />
                              <span className="text-lg font-bold text-green-600">{customer.live_speed?.download_mbps || customer.speed_test?.download_mbps}</span>
                              <span className="text-xs text-gray-500">Mbps</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Upload className="h-4 w-4 text-blue-500" />
                              <span className="text-lg font-bold text-blue-600">{customer.live_speed?.upload_mbps || customer.speed_test?.upload_mbps}</span>
                              <span className="text-xs text-gray-500">Mbps</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Connected Devices */}
                        <div className="bg-white/50 rounded-lg p-2 mb-3">
                          <div className="flex items-center justify-between text-xs mb-2">
                            <span className="text-gray-500 font-medium flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              Connected Devices
                            </span>
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">
                              {customer.connected_devices?.total_count || 0}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1 bg-blue-50 rounded px-2 py-1">
                              <Wifi className="h-3 w-3 text-blue-500" />
                              <span className="font-medium text-blue-700">{customer.connected_devices?.wifi?.length || 0}</span>
                              <span className="text-blue-500">WiFi</span>
                            </div>
                            <div className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                              <Cable className="h-3 w-3 text-gray-600" />
                              <span className="font-medium text-gray-700">{customer.connected_devices?.lan?.length || 0}</span>
                              <span className="text-gray-500">LAN</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Usage Stats */}
                        <div className="bg-white/50 rounded-lg p-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-500 font-medium">Monthly Usage</span>
                            <span className="text-gray-600 font-medium">{customer.usage_stats?.monthly_gb} GB</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-purple-400 to-indigo-500 h-2 rounded-full"
                              style={{ width: `${Math.min(customer.usage_stats?.usage_percent || 0, 100)}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                            <span>{customer.usage_stats?.usage_percent}%</span>
                            <span>{customer.usage_stats?.monthly_limit_gb} GB limit</span>
                          </div>
                        </div>
                        
                        {/* View Report Button */}
                        <Button
                          size="sm"
                          className="w-full mt-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchMonthlyReport(customer.customer_id);
                          }}
                        >
                          <BarChart3 className="h-4 w-4 mr-1" />
                          View Report
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Selected Customer Details */}
              {selectedCustomer && (
                <div className="mt-4 bg-white rounded-xl p-4 shadow-md border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-purple-500" />
                      {selectedCustomer.customer_name} - Connected Devices
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCustomer(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* WiFi Devices */}
                    <div>
                      <h5 className="font-medium text-blue-600 mb-2 flex items-center gap-1">
                        <Wifi className="h-4 w-4" />
                        WiFi Devices ({selectedCustomer.connected_devices?.wifi?.length || 0})
                      </h5>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedCustomer.connected_devices?.wifi?.map((device, idx) => (
                          <div key={idx} className="bg-blue-50 rounded-lg p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800 flex items-center gap-1">
                                <Smartphone className="h-3 w-3" />
                                {device.name}
                              </span>
                              <Signal className="h-3 w-3 text-green-500" />
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              <span>{device.ip_address}</span>
                              <span className="mx-2">•</span>
                              <span>{device.data_used_mb} MB used</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* LAN Devices */}
                    <div>
                      <h5 className="font-medium text-gray-600 mb-2 flex items-center gap-1">
                        <Cable className="h-4 w-4" />
                        LAN Devices ({selectedCustomer.connected_devices?.lan?.length || 0})
                      </h5>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedCustomer.connected_devices?.lan?.map((device, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800 flex items-center gap-1">
                                <Monitor className="h-3 w-3" />
                                {device.name}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              <span>{device.ip_address}</span>
                              <span className="mx-2">•</span>
                              <span>{device.data_used_mb} MB used</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* No Data State */}
              {!bandwidthData?.customers?.length && !loading && (
                <div className="text-center py-6">
                  <Activity className="h-12 w-12 text-purple-300 mx-auto mb-2" />
                  <p className="text-purple-700">No bandwidth data available</p>
                  <p className="text-xs text-purple-500 mt-1">
                    Customer bandwidth data will appear here
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Monthly Report Modal */}
      {showReportModal && reportData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 rounded-t-xl sticky top-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg">Monthly Bandwidth Report</h3>
                  <p className="text-purple-100 text-sm">{reportData.customer_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="bg-white/20 hover:bg-white/30 text-white"
                    onClick={() => downloadPDF(reportData.customer_id)}
                  >
                    <FileDown className="h-4 w-4 mr-1" />
                    Download PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                    onClick={() => setShowReportModal(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Customer Name</p>
                  <p className="font-medium text-gray-800">{reportData.customer_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">IP Address</p>
                  <p className="font-medium text-gray-800">{reportData.ip_address}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium text-gray-800">{reportData.location}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Office Name</p>
                  <p className="font-medium text-gray-800">{reportData.office_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Internet Provider</p>
                  <p className="font-medium text-gray-800">{reportData.isp}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Report Period</p>
                  <p className="font-medium text-gray-800">{reportData.report_period?.month_name} {reportData.report_period?.year}</p>
                </div>
              </div>
              
              {/* Usage Summary */}
              <h4 className="font-bold text-gray-800 mb-3">Usage Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-4 text-center">
                  <Download className="h-6 w-6 text-green-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-700">{reportData.summary?.total_download_gb}</p>
                  <p className="text-xs text-green-600">Download (GB)</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-sky-100 rounded-xl p-4 text-center">
                  <Upload className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-blue-700">{reportData.summary?.total_upload_gb}</p>
                  <p className="text-xs text-blue-600">Upload (GB)</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl p-4 text-center">
                  <TrendingUp className="h-6 w-6 text-purple-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-purple-700">{reportData.summary?.total_usage_gb}</p>
                  <p className="text-xs text-purple-600">Total Usage (GB)</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-xl p-4 text-center">
                  <Gauge className="h-6 w-6 text-amber-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-amber-700">{reportData.summary?.avg_speed_mbps}</p>
                  <p className="text-xs text-amber-600">Avg Speed (Mbps)</p>
                </div>
              </div>
              
              {/* Usage Progress */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Monthly Usage Progress</span>
                  <span className="text-sm text-gray-600">{reportData.summary?.usage_percent}% of {reportData.summary?.plan_limit_gb} GB</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-purple-400 via-violet-500 to-indigo-500 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(reportData.summary?.usage_percent || 0, 100)}%` }}
                  />
                </div>
              </div>
              
              {/* Daily Usage Chart */}
              <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                Daily Usage
                <span className="text-xs font-normal text-gray-500">({reportData.daily_data?.length || 0} days)</span>
              </h4>
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 overflow-x-auto">
                <div className="flex items-end gap-1 min-w-[600px]" style={{ height: '160px' }}>
                  {reportData.daily_data?.map((day, idx) => {
                    const maxUsage = Math.max(...reportData.daily_data.map(d => d.total_gb), 1);
                    const heightPercent = (day.total_gb / maxUsage) * 100;
                    const heightPx = Math.max((heightPercent / 100) * 140, 4);
                    return (
                      <div key={idx} className="flex flex-col items-center justify-end flex-1" style={{ height: '160px' }}>
                        <div 
                          className="w-full bg-gradient-to-t from-purple-500 to-indigo-400 rounded-t transition-all hover:from-purple-600 hover:to-indigo-500 cursor-pointer"
                          style={{ height: `${heightPx}px` }}
                          title={`${day.date}: ${day.total_gb} GB`}
                        />
                        <span className="text-[8px] text-gray-400 mt-1 whitespace-nowrap">
                          {day.date.split('-')[2]}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
                  <span>0 GB</span>
                  <span>{Math.max(...(reportData.daily_data?.map(d => d.total_gb) || [0])).toFixed(0)} GB</span>
                </div>
              </div>
              
              {/* Hourly Distribution */}
              <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                Hourly Usage Pattern
                <span className="text-xs font-normal text-gray-500">(24-hour distribution)</span>
              </h4>
              <div className="bg-white border border-gray-200 rounded-xl p-4 overflow-x-auto">
                <div className="flex items-end gap-1 min-w-[600px]" style={{ height: '120px' }}>
                  {reportData.hourly_distribution?.map((hour, idx) => {
                    const heightPx = Math.max((hour.usage_percent / 100) * 100, 2);
                    return (
                      <div key={idx} className="flex flex-col items-center justify-end flex-1" style={{ height: '120px' }}>
                        <div 
                          className="w-full bg-gradient-to-t from-blue-400 to-cyan-400 rounded-t transition-all hover:from-blue-500 hover:to-cyan-500 cursor-pointer"
                          style={{ height: `${heightPx}px` }}
                          title={`${hour.hour}: ${hour.usage_percent}%`}
                        />
                        <span className="text-[8px] text-gray-400 mt-1">
                          {idx % 4 === 0 ? hour.hour.replace(':00', '') : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandwidthMonitoring;
